import { Logger } from '@vimeo/logging';

import { DebugControlManager } from './DebugControlManager';
import { localStorageConfig } from '../../configs/LocalStorageConfig';
import { downloadFileByBlob } from '../../utils/general/download';
import { getCircularReplacer } from '../../utils/general/json';
import { setLocalStorageItem } from '../../utils/general/local_storage';

/**
 * Toggle current debugging state.
 */
export function toggleDebugState(
  manager: DebugControlManager,
  isEnabled: boolean,
  remember: boolean = true,
): void {
  manager.log.info('Toggling debug mode:', isEnabled);

  if (remember) {
    setLocalStorageItem(localStorageConfig.DEBUG.ENABLED, isEnabled);
  }

  manager.setContext({ isEnabled });
}

/**
 * Toggle current logging state.
 */
export function toggleLoggingState(
  manager: DebugControlManager,
  isEnabled: boolean,
  remember: boolean = true,
): void {
  Logger.IS_CONSOLE_ENABLED = isEnabled;
  Logger.IS_MARKING_ENABLED = isEnabled;

  if (remember) {
    setLocalStorageItem(localStorageConfig.DEBUG.LOGGING, isEnabled);
  }

  manager.log.info('Setting logging mode:', isEnabled);
  manager.setContext({ isLoggingEnabled: isEnabled });
}

/**
 * Toggle current file logging state.
 */
export function toggleFileLoggingState(
  manager: DebugControlManager,
  isEnabled: boolean,
  remember: boolean = true,
): void {
  manager.log.info('Toggling file reporting, state:', isEnabled);

  Logger.IS_FILE_ENABLED = isEnabled;

  if (remember) {
    setLocalStorageItem(localStorageConfig.DEBUG.FILE_LOGGING, isEnabled);
  }

  manager.setContext({ isFileLoggingEnabled: isEnabled });
}

/**
 * Save report file.
 */
export function saveReportFile(manager: DebugControlManager): void {
  if (!manager.context.isFileLoggingEnabled) {
    return;
  }

  const date: string = new Date().toLocaleString('en-GB');
  const fileName: string = `LIV_LOG_${date}.txt`;
  const reportInfo: Array<string> = [
    `Url: ${window.location.href}\n`,
    `Date: ${date}\n\n`,
    ...Logger.LOG_FILE_BUFFER,
    '\n\nState snapshot:\n',
  ];

  for (const [key, value] of manager
    .getScope()
    .INTERNAL.REGISTRY.CONTEXT_STATES_REGISTRY.entries()) {
    reportInfo.push(`[🌪${key.name}]: ${JSON.stringify(value, getCircularReplacer(), 2)}\n\n`);
  }

  manager.log.info('Saving file:', date);
  downloadFileByBlob(fileName, new Blob(reportInfo, { type: 'text/plain' }));
}

/**
 * Clear file logs to capture only required ones.
 */
export function clearLogs(manager: DebugControlManager): void {
  manager.log.info('Clearing up file logs');
  Logger.clearLogs();
}
